<template>
  <div class="weekly-status-page" :class="{ 'dark-mode': isDarkMode }">
    <div v-if="loading" class="app-container">
        <!-- Loading message while verifying access -->
        <p>Kontrollerar behörighet...</p>
    </div>

    <!-- <div v-else-if="accessGranted" class="app-container" :class="{ 'teams-view': isTeams }"> -->
        <div v-if="isDemoMode" class="demo-banner" role="status">
            Demoläge: alla ändringar stannar i den här webbläsaren.
        </div>
        <div class="header-container">
            <div class="header-content">
                <p class="header-location">CONSID · LIDKÖPING</p>
                <h1>Veckoschema</h1>
                <p class="header-dates">{{ formattedWeekDates }}</p>
            </div>
            <button
                class="theme-toggle"
                type="button"
                :aria-pressed="isDarkMode"
                @click="toggleDarkMode"
            >
                {{ isDarkMode ? '☀ Ljust läge' : '☾ Mörkt läge' }}
            </button>
        </div>

        <section v-if="weekDays.length" class="week-plan">
            <div class="week-plan-heading">
                <div>
                    <h2>Det här händer i veckan</h2>
                </div>
                <button v-if="canEditWeekPlan" class="button-secondary" type="button" @click="isEditingWeekPlan = !isEditingWeekPlan">
                    {{ isEditingWeekPlan ? 'Klar' : 'Redigera veckans plan' }}
                </button>
            </div>

            <div class="week-plan-grid">
                <article v-for="day in weekDays" :key="day.format('YYYY-MM-DD')" class="day-plan-card">
                    <p class="day-plan-date">{{ capitalize(day.format('dddd')) }} {{ day.format('D') }}</p>
                    <textarea
                        v-if="isEditingWeekPlan"
                        v-model="messages[day.format('YYYY-MM-DD')]"
                        class="day-plan-editor"
                        :aria-label="`Plan för ${day.format('dddd D MMMM')}`"
                        placeholder="Inget planerat ännu"
                    ></textarea>
                    <p v-else class="day-plan-text">
                        {{ messages[day.format('YYYY-MM-DD')] || 'Inget planerat ännu.' }}
                    </p>
                    <label v-if="day.day() === 5 && isEditingWeekPlan" class="lunch-host-picker">
                        <span>🍽 Lunchansvarig</span>
                        <select v-model="lunchHostId">
                            <option :value="null">Välj person</option>
                            <option v-for="user in users" :key="user.UserId" :value="user.UserId">
                                {{ user.Name }}
                            </option>
                        </select>
                    </label>
                </article>
            </div>

            <div v-if="isEditingWeekPlan && canEditWeekPlan" class="week-plan-actions">
                <button class="button-secondary" type="button" @click="copyPreviousWeekPlan">Kopiera planen från föregående vecka</button>
                <button type="button" @click="saveData" :disabled="!hasChanges">Spara veckans plan</button>
            </div>
        </section>

        <div v-if="users.length && weekDays.length" class="weekly-status">
            <section v-if="isMobile" class="mobile-schedule" aria-label="Veckoschema för mobil">
                <div class="mobile-day-tabs" role="tablist" aria-label="Välj dag">
                    <button
                        v-for="day in weekDays"
                        :key="day.format('YYYY-MM-DD')"
                        type="button"
                        role="tab"
                        :aria-selected="selectedMobileDay?.isSame(day, 'day')"
                        :class="{ active: selectedMobileDay?.isSame(day, 'day'), today: isToday(day) }"
                        @click="selectedMobileDate = day.format('YYYY-MM-DD')"
                    >
                        <span>{{ capitalize(day.format('dddd')).slice(0, 3) }}</span>
                        <strong>{{ day.format('D') }}</strong>
                    </button>
                </div>

                <p class="mobile-selected-day">
                    {{ capitalize(selectedMobileDay.format('dddd D MMMM')) }}
                </p>

                <div class="mobile-user-list">
                    <article
                        v-for="user in users"
                        :key="user.UserId"
                        :class="['mobile-user-row', 'tooltip-cell', { 'status-readonly': !canEditStatus(user) }]"
                        @click="canEditStatus(user) && cycleStatus(user.UserId, selectedMobileDay)"
                        @contextmenu.prevent="canEditStatus(user) && openStatusPicker(user.UserId, selectedMobileDay)"
                    >
                        <span class="mobile-user-identity">
                            <span class="mobile-user-name">{{ user.Name }}</span>
                            <span
                                v-if="selectedMobileDay.day() === 5 && user.UserId === lunchHostId"
                                class="mobile-lunch-host"
                                role="img"
                                aria-label="Väljer fredagslunch"
                            >👨‍🍳 Väljer lunch</span>
                        </span>
                        <span :class="['mobile-user-status', getStatusClass(getStatus(user.UserId, selectedMobileDay))]">
                            <img :src="getStatusIcon(getStatus(user.UserId, selectedMobileDay))" alt="" class="icon" />
                            <span>{{ getStatus(user.UserId, selectedMobileDay).StatusName || 'Ej uppdaterat' }}</span>
                        </span>

                        <div
                            v-if="isStatusPickerOpen(user.UserId, selectedMobileDay)"
                            class="status-picker"
                            role="menu"
                            @click.stop
                        >
                            <p class="status-picker-title">Välj status</p>
                            <button
                                v-for="status in availableStatuses"
                                :key="status.StatusId"
                                class="status-option"
                                type="button"
                                role="menuitem"
                                @click="selectStatus(user.UserId, selectedMobileDay, status)"
                            >
                                <img :src="getStatusIcon(status)" alt="" class="status-option-icon" />
                                <span>{{ status.StatusName }}</span>
                            </button>
                        </div>
                    </article>
                </div>
            </section>

            <table v-else class="weekly-table">
                <thead>
                    <tr>
                        <th class="name-column">Namn</th>
                        <th
                            v-for="day in weekDays"
                            :key="day"
                            :class="['day-column', { 'today-column': isToday(day) }]"
                        >
                            {{ capitalize(day.format('dddd')) }} {{ day.format('D') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in displayNames" :key="user.UserId">
                    <td
                        class="name-column"
                        tabindex="0"
                        :aria-label="`Visa profilbild för ${user.Name}`"
                        @mousemove="positionAvatarPreview($event)"
                        @focus="positionAvatarPreviewFromElement($event)"
                    >
                        <span class="person-name">
                            <img :src="getAvatar(user.Name)" alt="" class="person-avatar" />
                            <span>{{ user.displayName }}</span>
                        </span>
                        <span class="person-avatar-preview" :style="avatarPreviewStyle" aria-hidden="true">
                            <img :src="getAvatar(user.Name)" alt="" />
                        </span>
                    </td>
                    <td 
                    v-for="day in weekDays" 
                    :key="day" 
                    :class="[
                        'day-column',
                        'tooltip-cell',
                        {
                            'today-column': isToday(day),
                            'lunch-host-cell': day.day() === 5
                                && user.UserId === lunchHostId
                                && !isStatusPickerOpen(user.UserId, day),
                            'status-readonly': !canEditStatus(user),
                        },
                    ]"
                    @click="canEditStatus(user) && cycleStatus(user.UserId, day)"
                    @contextmenu.prevent="canEditStatus(user) && openStatusPicker(user.UserId, day)"
                    :aria-label="canEditStatus(user)
                        ? `Byt din status för ${day.format('dddd D MMMM')}`
                        : `${user.Name}s status för ${day.format('dddd D MMMM')}`"
                    :aria-expanded="isStatusPickerOpen(user.UserId, day)"
                    >

                    <span :class="['status-tag', getStatusClass(getStatus(user.UserId, day))]">
                        <img :src="getStatusIcon(getStatus(user.UserId, day))" alt="status icon" class="icon" />
                    </span>
                    <span
                        v-if="day.day() === 5 && user.UserId === lunchHostId"
                        class="lunch-host-marker"
                        role="img"
                        aria-label="Väljer fredagslunch"
                    >👨‍🍳</span>

                    <div
                        v-if="isStatusPickerOpen(user.UserId, day)"
                        class="status-picker"
                        role="menu"
                        @click.stop
                    >
                        <p class="status-picker-title">Välj status</p>
                        <button
                            v-for="status in availableStatuses"
                            :key="status.StatusId"
                            class="status-option"
                            type="button"
                            role="menuitem"
                            @click="selectStatus(user.UserId, day, status)"
                        >
                            <img :src="getStatusIcon(status)" alt="" class="status-option-icon" />
                            <span>{{ status.StatusName }}</span>
                        </button>
                    </div>

                    </td>
                    </tr>
                </tbody>
            </table>

            <!-- Legend for status icons -->
            <div class="legend">
                <div v-for="status in availableStatuses" :key="status.StatusId" class="legend-item">
                    <img :src="getStatusIcon({ StatusId: status.StatusId })" style="height:16px; max-height: 16px; max-width:16px; width: 16px;" alt="status icon" class="icon" />
                    <span>{{ status.StatusName }}</span>
                </div>
            </div>
            <p class="status-help">Du kan ändra din egen status. Klicka för nästa status, eller högerklicka/håll inne för ett specifikt val.</p>

            <section class="daily-summary" aria-label="Översikt över närvaro på Consid">
                <div class="daily-summary-heading">
                    <p class="eyebrow">NÄRVARO PÅ KONTORET</p>
                    <h2>Hur många är på Consid?</h2>
                </div>
                <article v-for="summary in dailySummaries" :key="summary.date" class="daily-summary-card">
                    <p>{{ summary.label }}</p>
                    <strong class="office-count">{{ summary.considCount }}</strong>
                    <span class="office-label">på Consid</span>
                    <span class="office-context">{{ summary.customerCount }} hos kund · {{ summary.homeCount }} hemma · {{ summary.unreportedCount }} ej uppdaterat</span>
                </article>
            </section>

            <hr>
            <br>
            <!-- Navigation buttons for weeks -->
            <div class="week-navigation">
                <button @click="previousWeek">Föregående vecka</button>
                <button @click="nextWeek">Nästa vecka</button>
            </div>
        </div>

        <footer class="footer-container">
            <!-- <img src="@/assets/consid_logo_big.png" alt="Consid Logo" class="footer-logo" /> -->
        </footer>
    <!-- </div> -->

    <!-- <div v-else class="app-container"> -->
        <!-- Access denied message -->
        <!-- <p>Åtkomst nekad. Du har inte behörighet att visa denna sida.</p> -->
    <!-- </div> -->
  </div>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/sv'; // Import your desired locales
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);

// icons:
import OfficeIcon from '@/assets/icons/customer.png';
import RemoteIcon from '@/assets/icons/home.png';
import VacationIcon from '@/assets/icons/vacation.png';
import SickLeaveIcon from '@/assets/icons/sick.png';
import ConsidOfficeIcon from '@/assets/icons/consid_logo.png';
import SickChildIcon from '@/assets/icons/vab.png';
import NoStatusIcon from '@/assets/icons/nostatus.png';
import NationalHolidayIcon from '@/assets/icons/national_holiday.png';
import AsaVallmarAvatar from '@/assets/avatars/asa_vallmar.jpg';
import CarlJohanNilssonAvatar from '@/assets/avatars/carl-johan_nilsson.jpg';
import DanielEkdahlAvatar from '@/assets/avatars/daniel_ekdahl.jpg';
import DanielThimAvatar from '@/assets/avatars/daniel_thim.jpg';
import HenrikDamgrenAvatar from '@/assets/avatars/henrik_damgren.jpg';
import JosefinLundgrenAvatar from '@/assets/avatars/josefin_lundgren.jpg';
import KalleEkelundAvatar from '@/assets/avatars/kalle_ekelund.jpg';
import ParLindquistAvatar from '@/assets/avatars/par_lindquist.jpg';
import ValdemarBorellAvatar from '@/assets/avatars/valdemar_borell.jpg';
import { demoDayInformation, demoStatusFor, demoStatuses, demoUsers } from '@/services/demoData';

const avatarsByName = {
  'Åsa Vallmar': AsaVallmarAvatar,
  'Carl-Johan Nilsson': CarlJohanNilssonAvatar,
  'Daniel Ekdahl': DanielEkdahlAvatar,
  'Daniel Thim': DanielThimAvatar,
  'Henrik Damgren': HenrikDamgrenAvatar,
  'Josefin Lundgren': JosefinLundgrenAvatar,
  'Kalle Ekelund': KalleEkelundAvatar,
  'Pär Lindquist': ParLindquistAvatar,
  'Valdemar Borell': ValdemarBorellAvatar,
};

// Extend dayjs with the weekOfYear plugin
dayjs.extend(weekOfYear);

export default {
  inject: ['account'],
  data() {
    return {
      users: [],
      statuses: {},           // Holds statuses for each user-day combination
      availableStatuses: [],   // Holds all statuses from the database
      weekDays: [],
      currentWeekNumber: dayjs().week(), // Start from the current week
      currentYear: dayjs().year(),
      isMobile: window.innerWidth <= 600, // Detect initial screen width
      isTeams: false,
      messages: {}, // Objekt för att spara text per dag
      originalMessages: {}, // Spara originalmeddelandena
      showFields: {}, // Hanterar vilka textfält som ska visas
      loading: true,
      isDemoMode: process.env.VUE_APP_DEMO_MODE === 'true',
      statusPicker: null,
      isEditingWeekPlan: false,
      selectedMobileDate: null,
      lunchHostId: null,
      originalLunchHostId: null,
      currentUserId: null,
      isDarkMode: localStorage.getItem('consid-theme') === 'dark',
      avatarPreviewPosition: { left: 0, top: 0 },
    };
  },
  computed: {
    formattedWeekDates() {
        if (this.weekDays.length === 0) return '';
        const start = this.weekDays[0].format('MMM D');
        const end = this.weekDays[this.weekDays.length - 1].format('MMM D, YYYY');
        return `${start} - ${end}`;
    },
    filteredUsers() {
        return this.users.filter(user => user != null);
    },
    displayNames() {
      return this.users.map((user) => ({
        ...user,
        displayName: this.isMobile ? this.getInitials(user.Name) : user.Name,
      }));
    },
    selectedMobileDay() {
        const selectedDay = this.weekDays.find(
            (day) => day.format('YYYY-MM-DD') === this.selectedMobileDate,
        );
        return selectedDay || this.weekDays.find((day) => this.isToday(day)) || this.weekDays[0];
    },
    avatarPreviewStyle() {
        return {
            left: `${this.avatarPreviewPosition.left}px`,
            top: `${this.avatarPreviewPosition.top}px`,
        };
    },
    lunchHost() {
        return this.users.find((user) => user.UserId === this.lunchHostId) || null;
    },
    hasChanges() {
        return JSON.stringify(this.messages) !== JSON.stringify(this.originalMessages)
            || this.lunchHostId !== this.originalLunchHostId;
    },
    canEditWeekPlan() {
        if (this.isDemoMode) return true;
        const editorOids = new Set([
            'c852c0ae-fbf8-4f0c-8f72-8d2461c677f1',
            'f6d59576-cc19-4ff7-98ea-179099a46ed7',
            '0a4f8f6f-a3f3-4726-a739-24c73e676031',
        ]);

        return editorOids.has(this.account?.idTokenClaims?.oid);
    },
    dailySummaries() {
        return this.weekDays.map((day) => {
            const date = day.format('YYYY-MM-DD');
            const entries = this.availableStatuses
                .map((status) => ({
                    name: status.StatusName,
                    statusId: status.StatusId,
                    people: this.users
                        .filter((user) => this.statuses[`${user.UserId}-${date}`]?.StatusId === status.StatusId)
                        .map((user) => user.Name),
                }))
                .map((entry) => ({ ...entry, count: entry.people.length }))
                .filter((entry) => entry.count > 0);

            return {
                date,
                label: `${this.capitalize(day.format('dddd'))} ${day.format('D')}`,
                total: entries.reduce((sum, entry) => sum + entry.count, 0),
                entries,
                considCount: entries.find((entry) => entry.name === 'Consid')?.count || 0,
                customerCount: entries.find((entry) => entry.name === 'Hos kund')?.count || 0,
                homeCount: entries.find((entry) => entry.name === 'Hemma')?.count || 0,
                unreportedCount: this.users.length - entries.reduce((sum, entry) => sum + entry.count, 0),
            };
        });
    }
  },
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  async created() {
    const userLanguage = navigator.language.startsWith('sv') ? 'sv' : 'en';

    this.weekDays.forEach(day => {
        const dateKey = day.format('YYYY-MM-DD');
        this.messages[dateKey] = '';
        this.showFields[dateKey] = false;
    });
    
    dayjs.locale(userLanguage);
    this.calculateCurrentWeek();
    await this.fetchUsers();
    await this.fetchCurrentUser();
    await this.fetchUserStatuses(); // Fetch all statuses for the week
    await this.fetchAvailableStatuses(userLanguage);
    await this.fetchDayInformation();
    await this.fetchLunchHost();

    this.originalMessages = JSON.parse(JSON.stringify(this.messages)); // make a copy
    this.originalLunchHostId = this.lunchHostId;
    this.loading = false;
  },
  methods: {
    toggleField(day) {
      const dateKey = day.format('YYYY-MM-DD');
      this.showFields[dateKey] = !this.showFields[dateKey];
    },
    getInitials(name) {
      return name
        .split(' ')
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
    },
    getAvatar(name) {
      return avatarsByName[name];
    },
    setAvatarPreviewPosition(clientX, clientY) {
      const previewSize = 158;
      const offset = 18;
      const viewportPadding = 12;
      this.avatarPreviewPosition = {
        left: Math.min(clientX + offset, window.innerWidth - previewSize - viewportPadding),
        top: Math.min(
          Math.max(clientY - previewSize / 2, viewportPadding),
          window.innerHeight - previewSize - viewportPadding,
        ),
      };
    },
    positionAvatarPreview(event) {
      this.setAvatarPreviewPosition(event.clientX, event.clientY);
    },
    positionAvatarPreviewFromElement(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.setAvatarPreviewPosition(rect.right, rect.top + rect.height / 2);
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 600;
    },
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem('consid-theme', this.isDarkMode ? 'dark' : 'light');
    },
    calculateCurrentWeek() {
      const startOfWeek = dayjs().year(this.currentYear).week(this.currentWeekNumber).startOf('isoWeek');
      this.weekDays = Array.from({ length: 5 }, (_, i) => startOfWeek.add(i, 'day'));
    },
    async nextWeek() {
        this.currentWeekNumber++;
        this.selectedMobileDate = null;
        this.calculateCurrentWeek();
        await Promise.all([this.fetchUserStatuses(), this.fetchDayInformation(), this.fetchLunchHost()]);
        this.originalMessages = JSON.parse(JSON.stringify(this.messages));
        this.originalLunchHostId = this.lunchHostId;
    },

    async previousWeek() {
        this.currentWeekNumber--;
        this.selectedMobileDate = null;
        this.calculateCurrentWeek();
        await Promise.all([this.fetchUserStatuses(), this.fetchDayInformation(), this.fetchLunchHost()]);
        this.originalMessages = JSON.parse(JSON.stringify(this.messages));
        this.originalLunchHostId = this.lunchHostId;
    },
    async saveData() {
        if (!this.canEditWeekPlan) return;
        const saveOperations = Object.entries(this.messages).flatMap(([date, textInfo]) => {
            if (this.originalMessages[date] !== textInfo) { // Only save if changed
                const saveOperation = this.saveDayInformation(date, textInfo);

                // Remove the date from showFields if text is empty
                if (!textInfo || textInfo.trim() === '') {
                    delete this.showFields[date];
                }
                return [saveOperation];
            }
            return [];
        });

        if (this.lunchHostId !== this.originalLunchHostId) {
            saveOperations.push(this.saveLunchHost());
        }

        await Promise.all(saveOperations);

        // Update originalMessages after saving
        this.originalMessages = JSON.parse(JSON.stringify(this.messages));
        this.originalLunchHostId = this.lunchHostId;
    },
    async copyPreviousWeekPlan() {
        if (!this.canEditWeekPlan) return;
        if (this.isDemoMode) {
            this.weekDays.forEach((day, index) => {
                this.messages[day.format('YYYY-MM-DD')] = demoDayInformation[index] || '';
            });
            return;
        }

        const previousWeek = this.weekDays.map((day) => day.subtract(1, 'week'));
        const start = previousWeek[0].format('YYYY-MM-DD');
        const end = previousWeek[previousWeek.length - 1].format('YYYY-MM-DD');

        try {
            const response = await axios.get(`${process.env.VUE_APP_API_URL}/dayInformation/${start}/${end}`);
            const previousMessages = new Map(
                response.data.map((entry) => [dayjs(entry.date).format('YYYY-MM-DD'), entry.textInfo]),
            );

            this.weekDays.forEach((day, index) => {
                this.messages[day.format('YYYY-MM-DD')] = previousMessages.get(previousWeek[index].format('YYYY-MM-DD')) || '';
            });
        } catch (error) {
            console.error('Error copying previous week information:', error);
        }
    },
    // Update weekDays and fetch statuses when changing weeks
    async updateWeek() {
        this.calculateCurrentWeek();
        await this.fetchUserStatuses();
    },
    capitalize(text) {
        return text.charAt(0).toUpperCase() + text.slice(1);
    },
    async fetchAvailableStatuses(lang = 'en') { // Default to 'en' if no language is specified
        if (this.isDemoMode) {
            this.availableStatuses = demoStatuses;
            return;
        }
        try {
            //console.log("Available statuses with translations start");
            const response = await axios.get(`${process.env.VUE_APP_API_URL}/statuses?lang=${lang}`);
            this.availableStatuses = response.data;
            //console.log("Available statuses with translations:", this.availableStatuses); // Check the data
        } catch (error) {
            console.error("Error fetching available statuses:", error);
        }
    },
    async fetchUsers() {
        if (this.isDemoMode) {
            this.users = demoUsers;
            return;
        }
        // console.log("Entering fetch users, api to use: ", process.env.VUE_APP_API_URL);
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/users`);
        this.users = response.data;
    },
    async fetchCurrentUser() {
        if (this.isDemoMode) {
            this.currentUserId = this.users.find((user) => user.Name === 'Daniel Ekdahl')?.UserId || null;
            return;
        }

        try {
            const response = await axios.get(`${process.env.VUE_APP_API_URL}/currentUser`);
            this.currentUserId = response.data.UserId;
        } catch (error) {
            this.currentUserId = null;
            console.error('Error fetching current user mapping:', error);
        }
    },
    async fetchUserStatuses() {
        if (this.isDemoMode) {
            this.users.forEach((user) => {
                this.weekDays.forEach((day, dayIndex) => {
                    const key = `${user.UserId}-${day.format('YYYY-MM-DD')}`;
                    this.statuses[key] = demoStatusFor(user.UserId, dayIndex);
                });
            });
            return;
        }
        const startOfWeek = this.weekDays[0].format('YYYY-MM-DD');
        const endOfWeek = this.weekDays[this.weekDays.length - 1].format('YYYY-MM-DD');
        const userLanguage = navigator.language.startsWith('sv') ? 'sv' : 'en'; // Default to Swedish if detected, otherwise English

        try {
            for (const user of this.users) {
                const response = await axios.get(`${process.env.VUE_APP_API_URL}/userStatus/${user.UserId}/${startOfWeek}/${endOfWeek}?lang=${userLanguage}`);
                
                const userStatuses = response.data;
                // let hasFridayStatus = false;

                // Populate statuses and check if Friday has a status
                userStatuses.forEach((statusEntry) => {
                    const formattedDate = dayjs(statusEntry.Date).format('YYYY-MM-DD');
                    const key = `${user.UserId}-${formattedDate}`;
                    this.statuses[key] = { StatusId: statusEntry.StatusId, StatusName: statusEntry.StatusName };

                    // // Check if this is a Friday status
                    // const dayOfWeek = dayjs(statusEntry.Date).day(); // 5 represents Friday
                    // if (dayOfWeek === 5) {
                    //     hasFridayStatus = true;
                    // }
                });

                // console.log("Before populating friday!");

                // // If Friday has no status, auto-fill with 'Consid Office' (StatusId 8)
                // if (!hasFridayStatus) {
                //     const friday = this.weekDays.find(day => day.day() === 5);
                //     if (friday) {
                //         const fridayKey = `${user.UserId}-${friday.format('YYYY-MM-DD')}`;
                //         this.statuses[fridayKey] = { StatusId: 5, StatusName: 'Consid Office' };
                        
                //         console.log("Will update userStatus with friday now!");

                //         // Optionally, send this status to the backend to persist it
                //         await axios.post(`${process.env.VUE_APP_API_URL}/createUserStatus`, {
                //             userId: user.UserId,
                //             day: friday.format('YYYY-MM-DD'),
                //             statusId: 14
                //         });
                //     }
                // }
            }
            //console.log("Populated statuses with auto-fill for missing Fridays:", this.statuses);
        } catch (error) {
            console.error("Error fetching user statuses:", error);
        }
    },
    async fetchDayInformation() {
        if (this.isDemoMode) {
            this.weekDays.forEach((day, dayIndex) => {
                const date = day.format('YYYY-MM-DD');
                const message = demoDayInformation[dayIndex];
                this.messages[date] = message;
                this.showFields[date] = Boolean(message);
            });
            return;
        }
        const startOfWeek = this.weekDays[0].format('YYYY-MM-DD');
        const endOfWeek = this.weekDays[this.weekDays.length - 1].format('YYYY-MM-DD');

        try {
            const response = await axios.get(`${process.env.VUE_APP_API_URL}/dayInformation/${startOfWeek}/${endOfWeek}`);
            
            // this.messages = response.data;

            response.data.forEach((entry) => {
                const formattedDate = dayjs(entry.date).format('YYYY-MM-DD');
                this.showFields[formattedDate] = true;
                this.messages[formattedDate] = entry.textInfo;
            });

            //console.log("Populated dayInformation:", this.messages);
        } catch (error) {
            console.error("Error fetching day information:", error);
        }
    },
    async fetchLunchHost() {
        const friday = this.weekDays.find((day) => day.day() === 5);
        this.lunchHostId = null;

        if (!friday) return;

        if (this.isDemoMode) {
            this.lunchHostId = this.users.find((user) => user.Name === 'Pär Lindquist')?.UserId || null;
            return;
        }

        const startOfWeek = this.weekDays[0].format('YYYY-MM-DD');
        const endOfWeek = this.weekDays[this.weekDays.length - 1].format('YYYY-MM-DD');

        try {
            const response = await axios.get(`${process.env.VUE_APP_API_URL}/lunchHosts/${startOfWeek}/${endOfWeek}`);
            const fridayHost = response.data.find((entry) =>
                dayjs(entry.Date || entry.date).isSame(friday, 'day'),
            );
            this.lunchHostId = fridayHost?.UserId || null;
        } catch (error) {
            console.error('Error fetching lunch host:', error);
        }
    },
    async saveDayInformation(day, text) {
        if (!day) return;
        const formattedDate = dayjs(day).format('YYYY-MM-DD');

        if (this.isDemoMode) return;

        console.log("1");
        try {
            // Save to the database
            await axios.post(`${process.env.VUE_APP_API_URL}/createDayInformation`, {
                day: formattedDate,
                text: text
            });

            // Directly update `statuses` in Vue 3
            // this.statuses[key] = { StatusId: nextStatus.StatusId, StatusName: nextStatus.StatusName };
        } catch (error) {
            console.error("Error creating dayInformation:", error);
        }
    },
    async saveLunchHost() {
        const friday = this.weekDays.find((day) => day.day() === 5);
        if (!friday || this.isDemoMode) return;

        const date = friday.format('YYYY-MM-DD');
        try {
            if (this.lunchHostId === null) {
                await axios.delete(`${process.env.VUE_APP_API_URL}/lunchHosts/${date}`);
                return;
            }

            await axios.put(`${process.env.VUE_APP_API_URL}/lunchHosts/${date}`, {
                userId: this.lunchHostId,
            });
        } catch (error) {
            console.error('Error saving lunch host:', error);
            throw error;
        }
    },
    getStatus(userId, day) {
        try {
            if (!userId || !day) return { StatusId: null, StatusName: '' };
            const key = `${userId}-${day.format('YYYY-MM-DD')}`;
            return this.statuses[key] || { StatusId: null, StatusName: '' };
        } catch (error) {
            console.error("Error in getStatus:", error);
            return { StatusId: null, StatusName: '' };
        }
    },
    getStatusIcon(status) {
        const minStatusId = Math.min(...this.availableStatuses.map(s => s.StatusId));
        switch (status.StatusId) {
            case minStatusId: return OfficeIcon;
            case minStatusId+1: return ConsidOfficeIcon;
            case minStatusId+2: return RemoteIcon;
            case minStatusId+3: return VacationIcon;
            case minStatusId+4: return SickLeaveIcon;
            case minStatusId+5: return SickChildIcon;
            case minStatusId+6: return NationalHolidayIcon;
            default: return NoStatusIcon;
        }
    },
    openStatusPicker(userId, day) {
        const dateStr = day.format('YYYY-MM-DD');
        const currentPicker = this.statusPicker;

        if (currentPicker?.userId === userId && currentPicker?.date === dateStr) {
            this.statusPicker = null;
            return;
        }

        this.statusPicker = { userId, date: dateStr };
    },
    isStatusPickerOpen(userId, day) {
        return this.statusPicker?.userId === userId
            && this.statusPicker?.date === day.format('YYYY-MM-DD');
    },
    isToday(day) {
        if (this.isDemoMode) return day.day() === 2;
        return day.isSame(dayjs(), 'day');
    },
    async cycleStatus(userId, day) {
        if (!this.availableStatuses.length || !this.canEditStatus({ UserId: userId })) return;

        const currentStatus = this.getStatus(userId, day);
        const currentIndex = this.availableStatuses.findIndex(
            (status) => status.StatusId === currentStatus.StatusId,
        );
        const nextStatus = this.availableStatuses[(currentIndex + 1) % this.availableStatuses.length];
        await this.selectStatus(userId, day, nextStatus);
    },
    async selectStatus(userId, day, nextStatus) {
        if (!userId || !day || !this.canEditStatus({ UserId: userId })) return;

        const dateStr = day.format('YYYY-MM-DD');
        const key = `${userId}-${dateStr}`;
        if (this.isDemoMode) {
            this.statuses[key] = nextStatus;
            this.statusPicker = null;
            return;
        }

        try {
            //console.log("Setting user status: ", nextStatus.StatusId)
            // Save to the database
            await axios.post(`${process.env.VUE_APP_API_URL}/createUserStatus`, {
                userId: userId,
                day: dateStr,
                statusId: nextStatus.StatusId
            });

            // Directly update `statuses` in Vue 3
            this.statuses[key] = { StatusId: nextStatus.StatusId, StatusName: nextStatus.StatusName };
            this.statusPicker = null;
        } catch (error) {
            console.error("Error setting status:", error);
        }
    },
    getStatusClass(status) {
        if (!status || !status.StatusId) return 'status-default';

        const minStatusId = Math.min(...this.availableStatuses.map(s => s.StatusId));

        switch (status.StatusId) {
            case minStatusId: // Office
                return 'status-office';
            case minStatusId+1: // Consid office
                return 'status-consid-office';
            case minStatusId+2: // Remote
                return 'status-remote';
            case minStatusId+3: // Vacation
                return 'status-vacation';
            case minStatusId+4: // Sick leave
                return 'status-sick-leave';
            case minStatusId+5: // Sick child
                return 'status-sick-child';
            case minStatusId+6: // Sick child
                return 'status-national-holiday';
            default:
                return 'status-default';
        }
    },
    canEditStatus(user) {
        return this.isDemoMode || user?.UserId === this.currentUserId;
    }
    }
};
</script>

<style scoped>
:global(html),
:global(body) {
    min-height: 100%;
    margin: 0;
}

:global(body) {
    font-family: 'Plus Jakarta Sans', sans-serif;
    background-color: #f7f9fc;
    color: #333;
}

h1 {
    color: #ffffff;
    text-align: center;
    padding: 20px 0;
    background-color: #701131; /* Vinröd for header background */
    font-size: 1.8em;
    font-weight: 700;
}

/* Mobile styling for phones */
@media (max-width: 768px) {
    /* Set a max-width for the weekly-status container */
    .weekly-status {
        width: 100%;
        max-width: 100%; /* Ensure it doesn’t exceed the screen width */
        overflow: hidden; /* Hide overflow to prevent extra space */
        padding: 0; /* Remove any padding around the container */
        margin: 0 auto; /* Center the container on the page */
    }

    /* Keep the table as a single row with horizontal scrolling */
    .weekly-table {
        display: block;
        width: 100%;
        overflow-x: auto; /* Enable horizontal scroll */
        font-size: 0.75em; /* Slightly smaller font size for compactness */
        white-space: nowrap; /* Prevent table cells from wrapping */
        padding: 0; /* Remove padding around the table */
        margin: 0; /* Remove margin around the table */
    }

    /* Each row remains in a single line, compacted */
    .weekly-table tr {
        display: table-row; /* Ensure rows are displayed inline */
    }

    /* Cells are further compacted */
    .weekly-table td {
        padding: 4px 2px; /* Reduced padding for a tighter fit */
        border: 1px solid #ddd;
        text-align: center;
    }

    /* Name column is even narrower */
    .name-column {
        min-width: 15px; /* Smaller minimum width */
        text-align: center;
        font-weight: bold;
        font-size: 0.7em; /* Smaller font for compact fit */
    }

    /* Day columns are also narrower */
    .day-column {
        min-width: 15px; /* Reduced width for day columns */
        padding: 3px 1px; /* Slightly reduced padding */
    }

    /* Icon size further reduced for compact view */
    .icon {
        width: 4px;
        height: 4px;
    }

    /* Self explanatory */
    .logo {
        width: 18px;
        height: 18px;
    }

    /* Hide table header on mobile for a cleaner look */
    .weekly-table thead {
        display: none;
    }

    /* Stack navigation buttons vertically with tighter spacing */
    .week-navigation {
        display: flex;
        flex-direction: column;
        gap: 4px; /* Reduced gap for compact layout */
        margin-bottom: 12px;
    }

    .week-navigation button {
        width: 100%;
        padding: 6px 0;
        font-size: 0.8em; /* Slightly smaller font size */
    }

    .textarea-day-information {
        width: 200px !important;
        height: 60px !important;;
        resize: none !important;;
        font-size: 12px !important;;
    }
}

/* Teams-specific styling */
.teams-view .header-container h1 {
    font-size: 1.5em;
}

.teams-view .weekly-status {
    padding: 8px;
}

.teams-view .week-navigation button {
    padding: 6px 10px;
    font-size: 0.8em;
}

.weekly-status {
    max-width: 1000px;
    width: 100%;
    margin: 15px auto;
    background-color: #ffffff;
    border-radius: 6px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);

    overflow-x: auto;
    overflow-y: visible;
}

.week-information {
  text-align: center;
}

.week-information-table {
  margin: 0 auto;
  border-collapse: collapse;
  width: 50%;
}

td {
  padding: 10px;
}

.day-name {
  text-align: right;
  font-weight: bold;
  width: 150px;
}

.textarea-day-information {
  width: 300px; 
  height: 60px;
  resize: none;
  font-size: 12px;
}

.weekly-table {
    width: 100%; /* Ensures table spans full width of container */
    table-layout: fixed; /* Fixes column width to make cells consistent */
    border-collapse: collapse;
    font-size: 0.85em;
}

.weekly-table th,
.weekly-table td {
    padding: 8px 10px;
    text-align: center;
    border: 1px solid #e0e0e0;
    white-space: nowrap;
    overflow: visible;
}

.weekly-table th {
    background-color: #374151;
    color: #ffffff;
    font-weight: bold;
}

.weekly-table td {
    position: relative;
    background-color: #f9fafb;
    cursor: pointer;
    transition: background-color 0.2s;
}

.weekly-table td:hover {
    background-color: #F49E88;
}

.weekly-table td.status-readonly,
.weekly-table td.status-readonly:hover {
    cursor: default;
    background-color: #f9fafb;
}

.weekly-table td.status-readonly.today-column:hover {
    background-color: #fff0f3;
}

.weekly-table td.status-readonly.lunch-host-cell:hover {
    background: #fff3cf;
}

.weekly-table th.today-column {
    background-color: #701131;
    box-shadow: inset 0 -4px 0 #f49e88;
}

.weekly-table td.today-column {
    background-color: #fff0f3;
}

.weekly-table td.today-column:hover {
    background-color: #fce0d8;
}

.weekly-table td.lunch-host-cell {
    overflow: hidden;
    background: #fff3cf;
}

.weekly-table td.lunch-host-cell:hover {
    background: #ffe7a4;
}

.weekly-table td.lunch-host-cell::before {
    content: '🍔  🍟  🍕  🍝  🍣';
    position: absolute;
    top: 50%;
    left: 0;
    z-index: 0;
    opacity: 0.34;
    font-size: 1.4rem;
    letter-spacing: 0.2rem;
    pointer-events: none;
    transform: translate(-105%, -50%);
    animation: lunch-parade 7s linear infinite;
    white-space: nowrap;
}

.weekly-table td.lunch-host-cell .status-tag,
.weekly-table td.lunch-host-cell .lunch-host-marker {
    z-index: 1;
}

@keyframes lunch-parade {
    from { transform: translate(-105%, -50%); }
    to { transform: translate(125%, -50%); }
}

.icon {
    width: 36px;
    height: 36px;
    filter: drop-shadow(0 0 1px black);
    user-select: none; /* Prevents selection */
    -webkit-user-select: none; /* Chrome/Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+ */
}

/* A small hint of motion helps the locations scan quickly without turning the table into a dashboard. */
.status-tag .icon {
    transform-origin: center;
    will-change: transform, filter;
}

.status-tag {
    position: relative;
    display: inline-flex;
}

.lunch-host-marker {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 1;
    display: grid;
    width: 25px;
    height: 25px;
    place-items: center;
    border: 1px solid #cb8e15;
    border-radius: 50%;
    background: #fff5cd;
    box-shadow: 0 2px 5px rgba(73, 43, 0, 0.3);
    font-size: 1rem;
    line-height: 1;
}

.status-office .icon {
    animation: customer-office-settle 4.8s ease-in-out infinite;
}

.status-consid-office .icon {
    animation: consid-office-pulse 3.6s ease-in-out infinite;
}

.status-remote .icon {
    animation: home-sway 5.2s ease-in-out infinite;
}

.status-vacation .icon {
    animation: vacation-glow 6s ease-in-out infinite;
}

@keyframes customer-office-settle {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
}

@keyframes consid-office-pulse {
    0%, 100% { transform: scale(1); filter: drop-shadow(0 0 1px black); }
    50% { transform: scale(1.1); filter: drop-shadow(0 0 5px rgba(112, 17, 49, 0.55)); }
}

@keyframes home-sway {
    0%, 100% { transform: rotate(0deg); }
    40% { transform: rotate(-2.5deg) translateY(-1px); }
    60% { transform: rotate(2.5deg); }
}

@keyframes vacation-glow {
    0%, 100% { transform: rotate(0deg) scale(1); filter: drop-shadow(0 0 1px black); }
    50% { transform: rotate(9deg) scale(1.1); filter: drop-shadow(0 0 6px rgba(255, 199, 0, 0.7)); }
}

@media (prefers-reduced-motion: reduce) {
    .status-tag .icon {
        animation: none;
    }

    .weekly-table td.lunch-host-cell::before {
        animation: none;
        transform: translate(-15%, -50%);
    }
}

/* Legend Styling */
.legend {
    margin-top: 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
}

.legend-item {
    display: flex;
    align-items: center;
    margin: 0 10px 10px;
}

.legend-item span {
    margin-left: 8px;
    color: #333;
    font-weight: 600;
}

.status-help {
    margin: -4px 16px 14px;
    color: #5b6472;
    font-size: 0.78rem;
    text-align: center;
}

.header-container {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0;
    padding: 26px;
    background: #701131;
    color: #ffffff;
    text-align: center;
}

.header-content {
    display: grid;
    gap: 3px;
}

.theme-toggle {
  position: absolute;
  top: 16px;
  right: 18px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
}

.theme-toggle:hover,
.theme-toggle:focus-visible {
  background: rgba(255, 255, 255, 0.24);
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

.header-location,
.header-dates {
    margin: 0;
}

.header-location {
    color: #f9dce5;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.11em;
}

.header-dates {
    color: #f7e7ed;
    font-size: 0.96rem;
}

.demo-banner {
    max-width: 1000px;
    margin: 12px auto 0;
    padding: 10px 16px;
    border: 1px solid #e5b759;
    border-radius: 6px;
    background: #fff6d8;
    color: #5c4200;
    font-weight: 700;
}

.header-container h1 {
    margin: 0;
    padding: 0;
    background: transparent;
    color: #ffffff;
    font-size: 2rem;
    font-weight: 700;
}

.footer-container {
    text-align: center;
    padding: 20px 0;
}

.footer-logo {
    height: 40px; /* Adjust as needed */
    opacity: 0.8; /* Optional: Add transparency if needed */
}

.week-navigation {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
}

button {
    background-color: #701131;
    color: #fff;
    border: none;
    padding: 10px 20px;
    margin: 0 5px;
    font-size: 1em;
    cursor: pointer;
    border-radius: 5px;
    transition: background-color 0.3s ease;
    font-family: 'Plus Jakarta Sans', sans-serif;
}

.addNewDayInformation {
    background-color: #701131;
    color: #fff;
    border: none;
    padding: 4px 10px;
    margin: 0 4px;
    font-size: 1em;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.3s ease;
    font-family: 'Plus Jakarta Sans', sans-serif;
}

.week-navigation button:hover {
    background-color: #F49E88; 
}

/* Name column takes up more space */
.name-column {
    position: relative;
    width: auto;
    text-align: left;
    padding-left: 12px;
    font-weight: bold;
}

.person-name {
  display: inline-flex;
  align-items: center;
  gap: 9px;
}

.person-avatar {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 1px 4px rgba(40, 20, 28, 0.24);
}

.person-avatar-preview {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10001;
  display: grid;
  width: 148px;
  height: 148px;
  padding: 4px;
  border: 1px solid #bd8c9e;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(35, 15, 24, 0.28);
  opacity: 0;
  pointer-events: none;
  transform: scale(0.96);
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.person-avatar-preview img {
  width: 100%;
  height: 100%;
  border-radius: 6px;
  object-fit: cover;
}

.weekly-table td.name-column:hover,
.weekly-table td.name-column:focus-visible {
  z-index: 2;
  outline: 2px solid #bd8c9e;
  outline-offset: -2px;
}

.weekly-table td.name-column:hover .person-avatar-preview,
.weekly-table td.name-column:focus-visible .person-avatar-preview {
  opacity: 1;
  transform: scale(1);
}

/* Day columns have a fixed width */
.day-column {
    width: 100px; /* Adjust width to fit your needs */
    padding: 4px;
    text-align: center;
}

.tooltip-cell {
  position: relative;
}

.status-picker {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  z-index: 10000;
  width: 190px;
  padding: 8px;
  border: 1px solid #d7dce3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  transform: translateX(-50%);
}

.status-picker-title {
  margin: 2px 6px 8px;
  color: #374151;
  font-size: 0.8rem;
  font-weight: 700;
}

.status-option {
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 7px 8px;
  border-radius: 4px;
  background: #ffffff;
  color: #1f2937;
  font-size: 0.85rem;
  text-align: left;
}

.status-option:hover,
.status-option:focus-visible {
  background: #fce5df;
  outline: 2px solid #701131;
  outline-offset: -2px;
}

.status-option-icon {
  width: 20px;
  height: 20px;
  margin-right: 8px;
  filter: none;
}

.week-information-header {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  color: #701131;
}

.week-information-header h2 {
  margin: 0;
  padding: 10px 0;
}

.arrow {
  font-size: 14px;
  color: #701131;
}

.week-plan {
  max-width: 1000px;
  box-sizing: border-box;
  margin: 18px auto;
  padding: 22px;
  border: 1px solid #eadde2;
  border-radius: 10px;
  background: #fffafb;
}

.week-plan-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.week-plan-heading h2 {
  margin: 0;
  color: #701131;
  font-size: 1.8rem;
}

.eyebrow {
  margin: 0 0 4px;
  color: #a31542;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.week-plan-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.day-plan-card {
  position: relative;
  padding: 12px;
  border: 1px solid #eadde2;
  border-radius: 8px;
  background: #ffffff;
}

.day-plan-date {
  margin: 0 0 8px;
  color: #701131;
  font-weight: 800;
}

.day-plan-text {
  margin: 0;
  color: #374151;
  font-size: 0.9rem;
  line-height: 1.45;
  white-space: pre-line;
}

.lunch-host-picker {
  display: grid;
  gap: 5px;
  margin-top: 12px;
  color: #6f4a00;
  font-size: 0.78rem;
  font-weight: 800;
}

.lunch-host-picker select {
  width: 100%;
  min-height: 32px;
  border: 1px solid #e1bd63;
  border-radius: 5px;
  background: #fffdf5;
  color: #374151;
  font: inherit;
  font-weight: 500;
}

.day-plan-editor {
  box-sizing: border-box;
  width: 100%;
  min-height: 76px;
  resize: vertical;
  border: 1px solid #c9bcc2;
  border-radius: 5px;
  padding: 8px;
  font: inherit;
}

.week-plan-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.button-secondary {
  border: 1px solid #701131;
  background: #ffffff;
  color: #701131;
}

.daily-summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #e5e7eb;
}

.daily-summary-heading {
  grid-column: 1 / -1;
  margin-bottom: 6px;
  text-align: center;
}

.daily-summary-heading h2 {
  margin: 0;
  font-size: 1.35rem;
}

.daily-summary-card {
  min-height: 112px;
  padding: 10px;
  border-radius: 6px;
  background: #f7f9fc;
  text-align: left;
}

.daily-summary-card p,
.daily-summary-card strong,
.daily-summary-card span {
  display: block;
}

.daily-summary-card p {
  margin: 0 0 4px;
  color: #701131;
  font-size: 0.78rem;
  font-weight: 800;
}

.daily-summary-card strong {
  color: #1f2937;
  font-size: 0.95rem;
}

.office-count {
  display: inline-block;
  color: #701131 !important;
  font-size: 2.2rem !important;
  line-height: 1;
}

.office-label,
.office-context {
  display: block;
}

.office-label {
  margin-top: 3px;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 800;
}

.office-context {
  margin-top: 9px;
  color: #5b6472;
  font-size: 0.76rem;
  line-height: 1.35;
}

.weekly-status-page.dark-mode {
  min-height: 100vh;
  background: #171317;
  color: #f3edf1;
}

.dark-mode .header-container {
  background: #5d0f2b;
}

.dark-mode .week-plan {
  border-color: #51404d;
  background: #211b22;
}

.dark-mode .week-plan-heading h2,
.dark-mode .day-plan-date,
.dark-mode .daily-summary-card p,
.dark-mode .office-count {
  color: #ffb3c7 !important;
}

.dark-mode .day-plan-card,
.dark-mode .weekly-status {
  background: #27212a;
}

.dark-mode .day-plan-card {
  border-color: #514552;
}

.dark-mode .day-plan-text,
.dark-mode .office-label,
.dark-mode .daily-summary-card strong,
.dark-mode .legend-item span {
  color: #edf0f5;
}

.dark-mode .weekly-table th {
  background: #39313d;
}

.dark-mode .weekly-table td,
.dark-mode .weekly-table td.status-readonly,
.dark-mode .weekly-table td.status-readonly:hover {
  border-color: #4b414e;
  background: #2b2530;
}

.dark-mode .weekly-table td:hover {
  background: #5b3243;
}

.dark-mode .weekly-table td.today-column,
.dark-mode .weekly-table td.status-readonly.today-column:hover {
  background: #492839;
}

.dark-mode .weekly-table td.lunch-host-cell,
.dark-mode .weekly-table td.lunch-host-cell:hover,
.dark-mode .weekly-table td.status-readonly.lunch-host-cell:hover {
  background: #4a3a26;
}

.dark-mode .daily-summary {
  border-top-color: #4b414e;
}

.dark-mode .daily-summary-card {
  background: #2b2530;
}

.dark-mode .office-context,
.dark-mode .status-help {
  color: #bac3d0;
}

.dark-mode .button-secondary,
.dark-mode .day-plan-editor,
.dark-mode .lunch-host-picker select {
  border-color: #806576;
  background: #302833;
  color: #edf0f5;
}

.dark-mode .status-picker {
  border-color: #5e5260;
  background: #2b2530;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.45);
}

.dark-mode .status-picker-title,
.dark-mode .status-option {
  color: #edf0f5;
}

.dark-mode .status-option {
  background: #2b2530;
}

.dark-mode .status-option:hover,
.dark-mode .status-option:focus-visible {
  background: #4a2b3b;
}

.dark-mode .person-avatar-preview {
  border-color: #9c7186;
  background: #302833;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.5);
}

.dark-mode .mobile-lunch-host {
  border-color: #c9942f;
  background: #44351e;
  color: #ffe6a1;
}

.location-group {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  margin-top: 9px;
}

.location-group img {
  width: 18px;
  height: 18px;
  margin-top: 1px;
}

.location-group div,
.location-group span {
  display: block;
}

.location-name {
  color: #374151;
  font-size: 0.78rem;
  font-weight: 800;
}

.location-people {
  margin-top: 2px;
  color: #5b6472;
  font-size: 0.76rem;
  line-height: 1.35;
}

@media (max-width: 768px) {
  .week-plan {
    margin: 12px;
    padding: 16px;
  }

  .week-plan-heading,
  .week-plan-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .week-plan-heading button,
  .week-plan-actions button {
    width: 100%;
    margin: 0;
  }

  .week-plan-grid,
  .daily-summary {
    grid-template-columns: 1fr;
  }

  .day-plan-card {
    min-height: auto;
  }

  .weekly-status {
    overflow: visible;
    margin: 12px;
    width: auto;
  }

  .mobile-schedule {
    overflow: visible;
  }

  .mobile-day-tabs {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 5px;
    padding: 10px;
    border-bottom: 1px solid #e5e7eb;
  }

  .mobile-day-tabs button {
    display: grid;
    gap: 2px;
    min-width: 0;
    margin: 0;
    padding: 8px 2px;
    border: 1px solid #d8c7ce;
    border-radius: 6px;
    background: #ffffff;
    color: #701131;
    font-size: 0.72rem;
    line-height: 1.1;
  }

  .mobile-day-tabs button strong {
    font-size: 1rem;
  }

  .mobile-day-tabs button.active {
    border-color: #701131;
    background: #701131;
    color: #ffffff;
  }

  .mobile-day-tabs button.today:not(.active) {
    border-color: #f49e88;
    background: #fff0f3;
  }

  .mobile-selected-day {
    margin: 0;
    padding: 14px 16px 8px;
    color: #374151;
    font-size: 0.92rem;
    font-weight: 800;
  }

  .mobile-user-list {
    padding: 0 10px 10px;
  }

  .mobile-user-row {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 58px;
    padding: 8px 10px;
    border-top: 1px solid #e5e7eb;
    cursor: pointer;
  }

  .mobile-user-row.status-readonly {
    cursor: default;
  }

  .mobile-user-name {
    min-width: 0;
    color: #1f2937;
    font-size: 0.92rem;
    font-weight: 700;
  }

  .mobile-user-identity {
    display: grid;
    min-width: 0;
    gap: 3px;
  }

  .mobile-lunch-host {
    width: fit-content;
    padding: 3px 6px;
    border: 1px solid #d2a33e;
    border-radius: 999px;
    background: #fff6d6;
    color: #785400;
    font-size: 0.68rem;
    font-weight: 800;
    white-space: nowrap;
  }

  .mobile-user-status {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 7px;
    color: #5b6472;
    font-size: 0.78rem;
    white-space: nowrap;
  }

  .mobile-user-status .icon {
    width: 30px;
    height: 30px;
  }

  .weekly-table {
    display: table;
    min-width: 680px;
    overflow: visible;
    font-size: 0.85rem;
  }

  .weekly-table thead {
    display: table-header-group;
  }

  .weekly-table td {
    padding: 8px 6px;
  }

  .weekly-table .name-column {
    min-width: 120px;
    font-size: 0.85rem;
  }

  .weekly-table .day-column {
    min-width: 82px;
  }

  .weekly-table .icon {
    width: 28px;
    height: 28px;
  }

  .dark-mode .mobile-day-tabs {
    border-bottom-color: #51404d;
  }

  .dark-mode .mobile-day-tabs button {
    border-color: #665361;
    background: #302833;
    color: #f6edf2;
  }

  .dark-mode .mobile-day-tabs button.active {
    border-color: #ffb3c7;
    background: #8b1d43;
    color: #ffffff;
  }

  .dark-mode .mobile-day-tabs button.today:not(.active) {
    border-color: #e694ad;
    background: #492839;
    color: #ffd4df;
  }

  .dark-mode .mobile-selected-day {
    color: #ffb3c7;
  }

  .dark-mode .mobile-user-row {
    border-top-color: #51404d;
  }

  .dark-mode .mobile-user-name {
    color: #f6edf2;
  }

  .dark-mode .mobile-user-status {
    color: #c9bcc4;
  }
}
</style>
